import React, { useEffect } from 'react';
import classnames from 'classnames';
import ReactTableWrapper from './ReactTableWrapper';
import SectionPagination from './SectionPagination';
import useManualReactTableState from './ManualReactTableState';
import {
    convertDataToListingDisplay,
    convertDispatchHistoryListingToAReadableFormat,
    getGraphQLTotalRecords,
} from '../selectors/converterSelector';
import {
    axiosRequestDispatchHistoryList,
} from '../actions/dispatchActions';
import LocalizedMessage from 'react-l10n';
import { breakpoints, getBreakpoint } from '../dom/_site';
import { handleAxiosErrors } from '../_utils';

/**
 * @param props
 * @returns {*}
 * @constructor
 */
function DispatchHistoryList(props) {
    let mainTableWrapper;

    const {
        className,
        requestPropWatcher = '',
        sortBy = 'Placed',
        sortDirection = 'DESC',
    } = props;

    // State management
    // --------------------
    const mrtState = useManualReactTableState({
        listParams: {
            sortBy,
            sortDirection,
        },
    });

    // Core
    const { isLoading, toggleLoading } = mrtState;
    const {
        list,
        listParams,
        getDisplayedList,
        setListDisplay,
        updateList,
    } = mrtState;
    const { getFilters, setFilters } = mrtState;
    const {
        getTotalRecords,
        setTotalRecords,
        getActivePage,
        setActivePage,
        getTotalPages,
        getPaginationPages,
    } = mrtState;
    const { handleOnTableUpdate } = mrtState;
    const listEffectWatcher = mrtState.getEffectWatcher();

    // User Input
    const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Effects
    // --------------------
    // - Main Request
    useEffect(() => {
        request();
    }, [listEffectWatcher, requestPropWatcher]);

    // Functions
    // --------------------
    /**
     * Request List
     */
    const request = () => {
        toggleLoading('list', true);

        return axiosRequestDispatchHistoryList(listParams)
            .then((response) => {
                const list = convertDispatchHistoryListingToAReadableFormat(
                    response.data.data.readCartOrders
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data.readCartOrders,
                    'ID'
                );
                const total = getGraphQLTotalRecords(
                    response.data.data.readCartOrders
                );

                console.log('list', list);

                // wait for async requests
                Promise.all([
                    setListDisplay(listingDisplay),
                    updateList(list),
                    setTotalRecords(total),
                ]).then(() => {
                    toggleLoading('list', false);
                });
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleLoading('list', false);
            });
    };

    /**
     * Get table props (for React Table)
     *
     * @returns {{list: {SubComponent: (function(*): *), columns: *[], showPagination: boolean, manual: boolean, getTrGroupProps: (function(*, *, *, *): {className: string})}}}
     */
    const getTableProps = () => {
        let tableProps = {
            list: {
                // manual: true,
                // noDataText: <span>No items found. <br/>See results from our full range below.</span>,
                columns: [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_NUMBER'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'ORDER_NO'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        <a href={`${window.baseURL}checkout/confirmation/${row.original.ID}/`}>{row.value}</a>
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DELIVER_TO'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'DeliverTo',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'CONTAINS'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ContainItemTypes',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'CARRIER'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ShippingMethod',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DATE_ORDERED'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PlacedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Placed',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ShippingStatus',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'BoxPackerParcelCount',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TRACKING'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info' style={{ wordBreak: 'break-all' }}>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'PostmenTrackingInfo',
                    },

                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ],
                showPagination: false,
                getTrGroupProps: (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];

                    let classes = 'react-table__row react-table__row--selectable';
                    classes += ` ${isExpanded ? 'react-table__row--expanded' : ''}`;

                    return {
                        className: classes,
                    };
                },
                SubComponent: (row) => {
                    const {
                        ID,
                        ContainItemTypes,
                        ShippingMethod,
                        ShippingStatus,
                        BoxPackerParcelCount,
                        PostmenTrackingInfo,
                    } = row.original;

                    return (
                        <div className='react-table__extra-data'>
                            <div className='row'>

                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'CONTAINS'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ContainItemTypes}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'CARRIER'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ShippingMethod}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </div>
                                    <span className='react-table__info'>
                                        {ShippingStatus}
                                    </span>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {BoxPackerParcelCount}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'TRACKING'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {PostmenTrackingInfo}
                                    </div>
                                </div>

                            </div>
                        </div>
                    );
                },
            },
        };

        return tableProps;
    };

    return (
        <React.Fragment>
            <div className={classnames('card card--border', className)}>
                <div className='card__header'>
                    <div className='row row--vcenter-md'>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <h2 className='heading heading--lv4'>
                                <LocalizedMessage id={'MANIFEST_LIST'} />
                            </h2>
                        </div>
                    </div>
                </div>
                <ReactTableWrapper
                    key={getBreakpoint()}
                    loading={isLoading.list}
                    onFetchData={handleOnTableUpdate}
                    data={getDisplayedList()}
                    reactTable={getTableProps()}
                    ref={(r) => (mainTableWrapper = r)}
                />
            </div>
            <SectionPagination
                activePage={getActivePage()}
                totalPages={getTotalPages()}
                pages={getPaginationPages()}
                onPaginationClick={(pageNum) => setActivePage(pageNum)}
            />
        </React.Fragment>
    );
}

export default DispatchHistoryList;
